import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Button, Row, Col, Form, Badge, Alert, Spinner, OverlayTrigger, Tooltip } from 'react-bootstrap';
import {
  GraduationCap, Plus, Trash2, Save, ShieldCheck, X, Pencil, RefreshCw, Info,
  ExternalLink, ChevronDown, ChevronUp, Eye, FileText,
} from 'lucide-react';
import Select from 'react-select';
import ReactMarkdown from 'react-markdown';
import gfm from 'remark-gfm';
import axios from '../../../../services/AxiosInstance';
import { getApiUrl } from '../../../../utils/ApiUtils';
import { useSettings } from '../../../../context/SettingsContext';
import InfoHint from '../../SharedComponents/InfoHint';
import { brandSelectTheme } from '../../Common/reactSelectTheme';
import ConfirmModal from '../../Common/ConfirmModal';

// Agent skills: the trust surface for what the agents know. Two provenance marks carry the
// whole model — green "Källverifierad" (LedgerLoop's shipped procedures, verified against the
// named authority on the shown date) vs neutral "Er egen rutin" (the company's own procedures,
// followed as company policy, never presented as regulatory fact). The platform owns all
// validation/clamps; a tenant routine can never widen tools or bypass approval limits.

const DEFAULT_LIMITS = { maxSkills: 20, nameMax: 48, titleMax: 80, descriptionMax: 160, bodyMax: 8000 };

// Swedish-friendly slug: accents transliterated (NFD, like PageHeader's slugify), kebab-cased.
// Slice BEFORE the final trim — a cut at a word boundary must not leave a trailing hyphen
// (the platform's kebab-case validation rejects it).
const slugify = (text, max) =>
  text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+/, '')
    .slice(0, max)
    .replace(/-+$/g, '');

// While TYPING in the slug field: same rules but keep a trailing hyphen, otherwise
// "betalnings-tid" is untypeable (the hyphen would vanish on every keystroke).
const slugifyWhileTyping = (text, max) =>
  text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9-]+/g, '-')
    .replace(/-{2,}/g, '-')
    .replace(/^-+/, '')
    .slice(0, max);

const emptyDraft = () => ({ name: '', title: '', description: '', agents: [], body: '' });

const EXAMPLE_BODY =
  '## När en kund begär förlängd betalningstid\n\n' +
  '1. Kolla först kundens historik — har kunden betalat sina tre senaste fakturor i tid?\n' +
  '2. I så fall: erbjud max 14 dagars förlängning, en gång per faktura.\n' +
  '3. Annars, eller vid större belopp än 20 000 kr: hänvisa alltid till Anna (ekonomiansvarig).\n' +
  '4. Notera alltid överenskommelsen på fakturan.';

const AgentSkillsSection = () => {
  const { companyId } = useSettings();
  const [removeTarget, setRemoveTarget] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [system, setSystem] = useState([]);
  const [tenant, setTenant] = useState([]);
  const [targetAgents, setTargetAgents] = useState([]);
  const [limits, setLimits] = useState(DEFAULT_LIMITS);
  const [editing, setEditing] = useState(null); // null | 'new' | skill name
  const [draft, setDraft] = useState(emptyDraft());
  const [slugTouched, setSlugTouched] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState([]);
  const [expanded, setExpanded] = useState(null); // `${kind}:${name}` of the opened card
  const [toggling, setToggling] = useState('');

  const load = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    setLoadError('');
    try {
      const { data } = await axios.get(getApiUrl(`${companyId}/agent-skills`), { withCredentials: true });
      setSystem(Array.isArray(data?.system) ? data.system : []);
      setTenant(Array.isArray(data?.tenant) ? data.tenant : []);
      setTargetAgents(Array.isArray(data?.targetAgents) ? data.targetAgents : []);
      setLimits({ ...DEFAULT_LIMITS, ...(data?.limits || {}) });
    } catch (err) {
      setLoadError(
        err?.response?.status === 403
          ? 'Agentrutiner ingår inte i din nuvarande plan.'
          : 'Kunde inte ladda agentrutiner.'
      );
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => { load(); }, [load]);

  const agentName = useMemo(() => {
    const byName = new Map(targetAgents.map((a) => [a.name, a]));
    return (name) => byName.get(name)?.displayName || name;
  }, [targetAgents]);

  const agentOptions = useMemo(
    () => targetAgents.map((a) => ({ value: a.name, label: a.role ? `${a.displayName} — ${a.role}` : a.displayName })),
    [targetAgents]
  );

  const startNew = () => {
    setErrors([]);
    setDraft(emptyDraft());
    setSlugTouched(false);
    setShowPreview(false);
    setEditing('new');
  };

  const startEdit = (skill) => {
    setErrors([]);
    // Drop targets that no longer exist in the roster (e.g. a deleted custom agent) —
    // the platform rejects them, which would otherwise block even a typo fix.
    const known = new Set(targetAgents.map((a) => a.name));
    setDraft({
      name: skill.name,
      title: skill.title || '',
      description: skill.description || '',
      agents: (skill.agents || []).filter((n) => known.has(n)),
      body: skill.body || '',
    });
    setShowPreview(false);
    setEditing(skill.name);
  };

  const cancel = () => { setEditing(null); setErrors([]); };

  const setTitle = (title) =>
    setDraft((d) => ({
      ...d,
      title,
      ...(editing === 'new' && !slugTouched ? { name: slugify(title, limits.nameMax) } : {}),
    }));

  const save = async () => {
    const name = slugify(draft.name, limits.nameMax); // final trim — no trailing hyphen from typing
    if (editing === 'new' && tenant.some((s) => s.name === name)) {
      setErrors([`En rutin med identifieraren "${name}" finns redan — välj ett annat namn eller redigera den befintliga.`]);
      return;
    }
    setSaving(true);
    setErrors([]);
    try {
      // `enabled` is deliberately NOT sent: the platform preserves the current flag
      // on content edits (a body fix must never silently re-enable a paused skill).
      await axios.put(
        getApiUrl(`${companyId}/agent-skills/${encodeURIComponent(name)}`),
        {
          title: draft.title.trim(),
          description: draft.description.trim(),
          agents: draft.agents,
          body: draft.body,
        },
        { withCredentials: true }
      );
      setEditing(null);
      await load();
    } catch (err) {
      const data = err?.response?.data;
      if (Array.isArray(data?.errors)) setErrors(data.errors);
      else setErrors(['Kunde inte spara rutinen. Försök igen.']);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (skill) => {
    try {
      await axios.delete(getApiUrl(`${companyId}/agent-skills/${encodeURIComponent(skill.name)}`), { withCredentials: true });
      await load();
    } catch (err) {
      setLoadError('Kunde inte ta bort rutinen. Försök igen.');
      throw err;
    }
  };

  const toggleEnabled = async (skill) => {
    setToggling(skill.name);
    try {
      await axios.post(
        getApiUrl(`${companyId}/agent-skills/${encodeURIComponent(skill.name)}/enabled`),
        { enabled: !skill.enabled },
        { withCredentials: true }
      );
      setTenant((prev) =>
        prev.map((s) =>
          s.name === skill.name ? { ...s, enabled: !skill.enabled, updatedAt: new Date().toISOString() } : s
        )
      );
    } catch (err) {
      setLoadError('Kunde inte ändra rutinens status. Försök igen.');
    } finally {
      setToggling('');
    }
  };

  const knownAgents = useMemo(() => new Set(targetAgents.map((a) => a.name)), [targetAgents]);

  const agentChips = (names) => (
    <div className="d-flex gap-1 flex-wrap">
      {(names || []).map((n) =>
        knownAgents.has(n) ? (
          <Badge key={n} bg="light" text="dark" style={{ fontSize: '0.72rem', fontWeight: 500, border: '1px solid #e5e7eb' }}>
            {agentName(n)}
          </Badge>
        ) : (
          // Target agent no longer exists (e.g. deleted custom agent) — say so instead
          // of showing a live-looking chip for an agent that will never see the skill.
          <Badge key={n} bg="warning" text="dark" style={{ fontSize: '0.72rem', fontWeight: 500 }}
            title="Agenten finns inte längre — rutinen når den inte. Redigera rutinen för att uppdatera.">
            {n} (borttagen)
          </Badge>
        )
      )}
    </div>
  );

  const expandKey = (kind, name) => `${kind}:${name}`;
  const bodyPanel = (body) => (
    <div style={{ borderTop: '1px solid #f3f4f6', marginTop: 12, paddingTop: 12, fontSize: '0.85rem', color: '#374151' }}>
      <ReactMarkdown remarkPlugins={[gfm]}>{body || ''}</ReactMarkdown>
    </div>
  );

  if (loading) {
    return (
      <div className="text-center py-5">
        <RefreshCw className="spin" size={24} />
        <p className="text-muted mt-2">Laddar agentrutiner...</p>
      </div>
    );
  }

  return (
    <>
      {/* Hero */}
      <div className="modern-card" style={{ background: 'var(--rgba-primary-1, rgba(68,129,78,0.08))', border: '1px solid var(--rgba-primary-2, rgba(68,129,78,0.2))' }}>
        <div className="modern-card-body d-flex align-items-center justify-content-between" style={{ padding: '24px 28px' }}>
          <div className="d-flex align-items-center gap-3">
            <div style={{ width: 52, height: 52, borderRadius: 14, background: 'var(--rgba-primary-2, rgba(68,129,78,0.18))', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--primary)' }}>
              <GraduationCap size={26} />
            </div>
            <div>
              <h2 className="mb-1" style={{ fontSize: '1.35rem', fontWeight: 600 }}>Agentrutiner</h2>
              <p className="mb-0 text-muted" style={{ fontSize: '0.9rem' }}>
                Se exakt vilka procedurer era agenter följer — och lär dem era egna rutiner.
              </p>
            </div>
          </div>
          {editing === null && (
            <Button variant="primary" onClick={startNew} style={{ minWidth: 130 }} disabled={tenant.length >= limits.maxSkills}>
              <Plus className="me-1" size={16} /> Ny rutin
            </Button>
          )}
        </div>
      </div>

      {/* The trust model, made visible */}
      <Alert variant="light" className="mt-3 d-flex align-items-start gap-2" style={{ border: '1px solid #e5e7eb', background: '#fafafa' }}>
        <ShieldCheck size={18} style={{ color: 'var(--primary)', flexShrink: 0, marginTop: 2 }} />
        <div style={{ fontSize: '0.82rem', color: '#4b5563' }}>
          <strong style={{ color: 'var(--primary)' }}>Källverifierad</strong> betyder att LedgerLoop har kontrollerat proceduren
          mot angiven myndighetskälla på angivet datum. <strong>Er egen rutin</strong> följs som företagets policy men presenteras
          aldrig som myndighetskrav — och den kan aldrig kringgå kravet på mänskligt godkännande. Det är kodtvingat i plattformen.
        </div>
      </Alert>

      {loadError && <Alert variant="warning" className="mt-2">{loadError}</Alert>}

      {/* Editor */}
      {editing !== null && (
        <div className="modern-card mt-3">
          <div className="modern-card-body" style={{ padding: '24px 28px' }}>
            <div className="d-flex align-items-center justify-content-between mb-3">
              <h5 className="mb-0" style={{ fontSize: '1rem', fontWeight: 600 }}>
                {editing === 'new' ? 'Ny rutin' : `Redigera ${draft.title || draft.name}`}
              </h5>
              <Button variant="link" className="text-muted p-0" onClick={cancel}><X size={20} /></Button>
            </div>

            {errors.length > 0 && (
              <Alert variant="danger">
                <ul className="mb-0" style={{ paddingLeft: 18 }}>
                  {errors.map((e, i) => <li key={i} style={{ fontSize: '0.82rem' }}>{e}</li>)}
                </ul>
              </Alert>
            )}

            <Alert variant="light" className="d-flex align-items-start gap-2" style={{ border: '1px solid #f3f4f6', background: '#fcfcfc' }}>
              <Info size={16} style={{ color: '#9ca3af', flexShrink: 0, marginTop: 2 }} />
              <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>
                Skriv rutinen som ni skulle förklara den för en ny kollega: när gäller den, stegen i ordning,
                och vad som kräver godkännande av en viss person. Agenten läser in rutinen när frågan dyker upp
                och följer den som er policy.
              </div>
            </Alert>

            <Row>
              <Col md={7}>
                <Form.Group className="mb-3">
                  <Form.Label style={{ fontSize: '0.85rem', fontWeight: 500 }}>Titel</Form.Label>
                  <Form.Control value={draft.title} placeholder="Förlängd betalningstid för kunder" maxLength={limits.titleMax}
                    onChange={(e) => setTitle(e.target.value)} style={{ borderRadius: 8 }} />
                </Form.Group>
              </Col>
              <Col md={5}>
                <Form.Group className="mb-3">
                  <Form.Label style={{ fontSize: '0.85rem', fontWeight: 500 }}>
                    Identifierare (slug)
                    <InfoHint text="Skapas från titeln. Kan inte ändras senare." />
                  </Form.Label>
                  <Form.Control value={draft.name} disabled={editing !== 'new'} placeholder="forlangd-betalningstid"
                    onChange={(e) => { setSlugTouched(true); setDraft((d) => ({ ...d, name: slugifyWhileTyping(e.target.value, limits.nameMax) })); }}
                    style={{ borderRadius: 8 }} />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <div className="d-flex align-items-center justify-content-between">
                <div className="d-flex align-items-center gap-1">
                  <Form.Label className="mb-0" style={{ fontSize: '0.85rem', fontWeight: 500 }}>Beskrivning</Form.Label>
                  <OverlayTrigger placement="top" overlay={<Tooltip>Visas i agentens verktygsmeny — det är så agenten vet när rutinen är relevant.</Tooltip>}>
                    <Info size={14} style={{ color: '#9ca3af', cursor: 'help' }} />
                  </OverlayTrigger>
                </div>
                <span style={{ fontSize: '0.72rem', color: draft.description.length > limits.descriptionMax ? '#dc3545' : '#9ca3af' }}>
                  {draft.description.length}/{limits.descriptionMax}
                </span>
              </div>
              <Form.Control as="textarea" rows={2} value={draft.description} maxLength={limits.descriptionMax}
                placeholder="Vår rutin när en kund ber om förlängd betalningstid — vem som får bevilja vad."
                onChange={(e) => setDraft((d) => ({ ...d, description: e.target.value }))} style={{ borderRadius: 8, marginTop: 4 }} />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label style={{ fontSize: '0.85rem', fontWeight: 500 }}>Gäller för agenter</Form.Label>
              <Select
                isMulti
                options={agentOptions}
                value={agentOptions.filter((o) => draft.agents.includes(o.value))}
                onChange={(vals) => setDraft((d) => ({ ...d, agents: (vals || []).map((v) => v.value) }))}
                placeholder="Välj minst en agent..."
                noOptionsMessage={() => 'Inga fler agenter'}
                theme={brandSelectTheme}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <div className="d-flex align-items-center justify-content-between">
                <Form.Label className="mb-0" style={{ fontSize: '0.85rem', fontWeight: 500 }}>Rutinen (markdown)</Form.Label>
                <Button variant="link" size="sm" className="p-0 text-muted" onClick={() => setShowPreview((p) => !p)}>
                  {showPreview ? <><Pencil size={13} className="me-1" />Redigera</> : <><Eye size={13} className="me-1" />Förhandsgranska</>}
                </Button>
              </div>
              {showPreview ? (
                <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: '12px 16px', marginTop: 4, minHeight: 220, fontSize: '0.85rem' }}>
                  <ReactMarkdown remarkPlugins={[gfm]}>{draft.body || '*Inget innehåll än.*'}</ReactMarkdown>
                </div>
              ) : (
                <Form.Control as="textarea" rows={10} value={draft.body} maxLength={limits.bodyMax}
                  placeholder={EXAMPLE_BODY}
                  onChange={(e) => setDraft((d) => ({ ...d, body: e.target.value }))}
                  style={{ borderRadius: 8, marginTop: 4, fontFamily: 'ui-monospace, monospace', fontSize: '0.82rem' }} />
              )}
              <Form.Text muted style={{ fontSize: '0.72rem' }}>
                {draft.body.length}/{limits.bodyMax} tecken
                <InfoHint text="Skriv steg, beloppsgränser och vem som beslutar — inte skattesatser eller lagregler (dem hämtar agenten ur den verifierade kunskapsbasen)." />
              </Form.Text>
            </Form.Group>

            <div className="d-flex justify-content-end gap-2 mt-4">
              <Button variant="outline-secondary" onClick={cancel} disabled={saving}>Avbryt</Button>
              <Button variant="primary" onClick={save}
                disabled={saving || !draft.name || !draft.title.trim() || draft.description.trim().length < 10 || draft.agents.length === 0 || draft.body.trim().length < 30}>
                {saving ? <Spinner size="sm" className="me-2" /> : <Save className="me-2" size={15} />}
                {saving ? 'Sparar...' : 'Spara rutin'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Tenant skills */}
      {editing === null && (
        <div className="mt-4">
          <h5 style={{ fontSize: '0.95rem', fontWeight: 600, marginBottom: 12 }}>Era egna rutiner</h5>

          {tenant.length === 0 && !loadError && (
            <div className="modern-card">
              <div className="modern-card-body text-center py-5">
                <div style={{ width: 56, height: 56, borderRadius: 16, background: 'var(--rgba-primary-1, rgba(68,129,78,0.1))', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--primary)', margin: '0 auto 12px' }}>
                  <FileText size={26} />
                </div>
                <h5 style={{ fontSize: '1rem', fontWeight: 600 }}>Lär era agenter era egna rutiner</h5>
                <p className="text-muted mx-auto" style={{ fontSize: '0.85rem', maxWidth: 480 }}>
                  Hur ni hanterar betalningspåminnelser, vem som attesterar representation, vad som gäller vid
                  utlägg — skriv ner rutinen en gång, så följer agenterna den i varje konversation.
                </p>
                <Button variant="primary" onClick={startNew}>
                  <Plus className="me-1" size={16} /> Skapa er första rutin
                </Button>
              </div>
            </div>
          )}

          {tenant.map((s) => (
            <div key={s.name} className="modern-card mb-2" style={{ opacity: s.enabled ? 1 : 0.6 }}>
              <div className="modern-card-body" style={{ padding: '16px 24px' }}>
                <div className="d-flex align-items-start justify-content-between gap-3">
                  <div style={{ minWidth: 0 }}>
                    <div className="d-flex align-items-center gap-2 flex-wrap">
                      <span style={{ fontWeight: 600 }}>{s.title}</span>
                      <Badge bg="light" text="dark" style={{ fontSize: '0.7rem', border: '1px solid #e5e7eb', fontWeight: 500 }}>
                        Er egen rutin
                      </Badge>
                      {!s.enabled && <Badge bg="secondary" style={{ fontSize: '0.7rem' }}>Pausad</Badge>}
                      {s.shadowed && (
                        <Badge bg="warning" text="dark" style={{ fontSize: '0.7rem' }}
                          title="LedgerLoop levererar numera en källverifierad procedur med samma identifierare — den gäller i stället för denna rutin. Byt namn på rutinen om den fortfarande behövs.">
                          Ersätts av inbyggd procedur
                        </Badge>
                      )}
                    </div>
                    <div className="text-muted" style={{ fontSize: '0.8rem', marginTop: 2 }}>{s.description}</div>
                    <div className="d-flex align-items-center gap-2 flex-wrap" style={{ marginTop: 6 }}>
                      {agentChips(s.agents)}
                      {s.updatedAt && (
                        <span className="text-muted" style={{ fontSize: '0.72rem' }}>
                          Uppdaterad {String(s.updatedAt).slice(0, 10)}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="d-flex align-items-center gap-2 flex-shrink-0">
                    <Form.Check type="switch" id={`enabled-${s.name}`} checked={!!s.enabled}
                      onChange={() => toggleEnabled(s)} disabled={toggling === s.name}
                      title={s.enabled ? 'Pausa rutinen' : 'Aktivera rutinen'} />
                    <Button variant="outline-secondary" size="sm" onClick={() => startEdit(s)}><Pencil size={14} /></Button>
                    <Button variant="outline-danger" size="sm" onClick={() => setRemoveTarget(s)}><Trash2 size={14} /></Button>
                    <Button variant="link" size="sm" className="text-muted p-0 d-inline-flex align-items-center gap-1"
                      style={{ fontSize: '0.78rem', textDecoration: 'none', whiteSpace: 'nowrap' }}
                      onClick={() => setExpanded(expanded === expandKey('t', s.name) ? null : expandKey('t', s.name))}>
                      {expanded === expandKey('t', s.name)
                        ? <>Dölj <ChevronUp size={14} /></>
                        : <>Visa <ChevronDown size={14} /></>}
                    </Button>
                  </div>
                </div>
                {expanded === expandKey('t', s.name) && bodyPanel(s.body)}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* System skills — the provenance surface */}
      {editing === null && system.length > 0 && (
        <div className="mt-4">
          <h5 style={{ fontSize: '0.95rem', fontWeight: 600, marginBottom: 4 }}>LedgerLoops verifierade procedurer</h5>
          <p className="text-muted" style={{ fontSize: '0.8rem', marginBottom: 12 }}>
            Procedurer era agenter redan kan — kontrollerade mot myndighetskällor. Klicka för att läsa exakt vad agenten följer.
          </p>

          {system.map((s) => (
            <div key={s.name} className="modern-card mb-2">
              <div className="modern-card-body" style={{ padding: '16px 24px' }}>
                <div className="d-flex align-items-start justify-content-between gap-3">
                  <div style={{ minWidth: 0 }}>
                    <div className="d-flex align-items-center gap-2 flex-wrap">
                      <ShieldCheck size={16} style={{ color: 'var(--primary)', flexShrink: 0 }} />
                      <span style={{ fontWeight: 600 }}>{s.title}</span>
                      <Badge style={{ fontSize: '0.7rem', fontWeight: 500, background: 'var(--rgba-primary-1, rgba(68,129,78,0.1))', color: 'var(--primary)' }} bg="">
                        {s.kind === 'workflow' ? 'Arbetsflöde — siffror från verktygen' : `Källverifierad ${s.verifiedAt}`}
                      </Badge>
                    </div>
                    <div className="text-muted" style={{ fontSize: '0.8rem', marginTop: 2 }}>{s.description}</div>
                    <div className="d-flex align-items-center gap-2 flex-wrap" style={{ marginTop: 6 }}>
                      {agentChips(s.agents)}
                      {(s.sources || []).map((src, i) => (
                        <a key={i} href={src.url} target="_blank" rel="noopener noreferrer"
                          className="d-inline-flex align-items-center gap-1"
                          style={{ fontSize: '0.72rem', color: 'var(--primary)', textDecoration: 'none', border: '1px solid var(--rgba-primary-2, rgba(68,129,78,0.2))', borderRadius: 12, padding: '1px 8px', background: 'var(--rgba-primary-1, rgba(68,129,78,0.07))' }}>
                          {src.authority} <ExternalLink size={10} />
                        </a>
                      ))}
                    </div>
                  </div>
                  <Button variant="outline-secondary" size="sm" className="flex-shrink-0 d-inline-flex align-items-center gap-1"
                    style={{ fontSize: '0.78rem', whiteSpace: 'nowrap' }}
                    onClick={() => setExpanded(expanded === expandKey('s', s.name) ? null : expandKey('s', s.name))}>
                    {expanded === expandKey('s', s.name)
                      ? <>Dölj proceduren <ChevronUp size={14} /></>
                      : <>Läs proceduren <ChevronDown size={14} /></>}
                  </Button>
                </div>
                {expanded === expandKey('s', s.name) && bodyPanel(s.body)}
              </div>
            </div>
          ))}
        </div>
      )}
      <ConfirmModal
        show={Boolean(removeTarget)}
        title="Ta bort rutinen?"
        body="Agenterna slutar följa rutinen direkt. Du kan lägga till den igen senare."
        detail={removeTarget?.title}
        confirmLabel="Ta bort rutin"
        onConfirm={async () => { await remove(removeTarget); setRemoveTarget(null); }}
        onCancel={() => setRemoveTarget(null)}
      />
    </>
  );
};

export default AgentSkillsSection;
